import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Footer() {
  const footerAnim = useScrollAnimation({ threshold: 0.1 });
  const [activeLink, setActiveLink] = useState<string | null>(null);

  const scrollToSection = (id: string) => {
    setActiveLink(id);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
      setTimeout(() => setActiveLink(null), 400);
    }, 300);
  };

  return (
    <footer className="bg-[#0A0A0A] text-white border-t border-white/5">
      <div ref={footerAnim.ref} className="max-w-7xl mx-auto px-5 lg:px-16 py-6 lg:py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 mb-5 lg:mb-6">
          {/* Brand */}
          <div
            className={`md:col-span-1 transition-all duration-700 ${footerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="flex items-center gap-2 lg:gap-3 mb-3">
              <img
                src="https://public.readdy.ai/ai/img_res/9de28202-fb2f-4afa-b431-f55bb048cea8.png"
                alt="ZEN Logo"
                className="h-7 lg:h-9 w-auto"
              />
              <img
                src="https://static.readdy.ai/image/7e22dd63abefb8d10cd003bf2bbcd2a0/c89518d45fe9de02ff846b57fb8e0e27.png"
                alt="Brand Logo"
                className="h-8 lg:h-10 w-auto invert brightness-0 invert -ml-1 lg:-ml-2 -mt-1 lg:-mt-2"
              />
            </div>
            <p className="text-white/50 text-xs leading-relaxed mb-3">
              Your trusted partner for premium building materials, fixtures, and interior finishes. Quality you can count on.
            </p>
            <div className="flex items-center gap-2 lg:gap-3">
              {['ri-facebook-fill', 'ri-instagram-fill', 'ri-twitter-fill', 'ri-youtube-fill'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-[#FF6600] rounded-full transition-all duration-200 cursor-pointer hover:scale-110"
                >
                  <i className={`${icon} text-xs`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div
            className={`transition-all duration-700 ${footerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '250ms' }}
          >
            <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-3">Quick Links</h3>
            <ul className="space-y-1.5">
              {[
                { label: 'Home', id: 'home' },
                { label: 'About Us', id: 'introduction' },
                { label: 'Products', id: 'products' },
                { label: 'Contact', id: 'contact' }
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className={`text-xs cursor-pointer flex items-center gap-2 transition-all duration-300 ${
                      activeLink === link.id
                        ? 'text-[#FF6600] scale-110 translate-x-3'
                        : 'text-white/50 hover:text-[#FF6600] hover:translate-x-1'
                    }`}
                  >
                    <i className={`ri-arrow-right-s-line transition-all duration-300 ${
                      activeLink === link.id
                        ? 'text-white rotate-90 scale-125'
                        : 'text-[#FF6600]'
                    }`}></i>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div
            className={`transition-all duration-700 ${footerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '400ms' }}
          >
            <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-3">Contact</h3>
            <ul className="space-y-2">
              {[
                { icon: 'ri-map-pin-line', text: 'Luisa St., Corner Mangga St., Juna, Subdivision Ecoland, Matina, Davao City' },
                { icon: 'ri-phone-line', text: '+63 (939) 939 8250' },
                { icon: 'ri-mail-line', text: 'southdevhomedepo2020@gmail.com' },
                { icon: 'ri-time-line', text: 'Mon\u2013Sat: 8:00 AM \u2013 5:00 PM' }
              ].map((item) => (
                <li key={item.icon} className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#FF6600] text-xs`}></i>
                  </div>
                  <span className="text-white/50 text-xs">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className={`border-t border-white/5 pt-4 flex flex-col md:flex-row items-center justify-between gap-2 transition-all duration-700 ${footerAnim.isVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '550ms' }}
        >
          <p className="text-white/30 text-xs">
            &copy; 2018 SouthDev Home Depot. All rights reserved.
          </p>
          <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="text-white/30 text-xs hover:text-white/50 transition-colors duration-200 cursor-pointer">
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
}
